import { useSelector } from 'react-redux'
import {
    changeMaintenanceMode,
    updateMaintenanceModeData,
    changeSalesPersonStatus,
    deleteAdmin,
    deleteCompany,
    deleteUser,
    getFeatureFlags,
    getUsers,
    refreshCompanyData,
    searchUserAndCompanies,
    toggleFeature,
    getReportUsersInfo,
    getReportUsersLoginInfo,
    getReportSalesRepLoginInfo,
    verifyCustomer,
    verifyCustomers,
} from '../repositories/AdminRepository'
import { useAdminActions } from '../store/slices/AdminSlice'

const useAdmin = () => {
    const {
        fetchCompanies,
        updateUserManagementStatus,
        deleteCompany: deleteCompanyStore,
        deleteUser: deleteUserStore,
        impersonateUser,
        stopImpersonation,
        forgotPassword,
        setCurrentCompanySapId,
    } = useAdminActions()

    const adminSuccess = useSelector(state => state.AdminSlice?.adminSuccess)
    const adminHasErrored = useSelector(
        state => state.AdminSlice?.adminHasErrored,
    )
    const adminIsLoading = useSelector(
        state => state.AdminSlice?.adminIsLoading,
    )
    const companies = useSelector(state => state.AdminSlice?.companies)
    const currentCompanySapId = useSelector(
        state => state.AdminSlice?.currentCompanySapId,
    )
    const errors = useSelector(state => state.AdminSlice?.errors)

    const checkForValidCompanyIds = companyIds =>
        new Promise((resolve, reject) =>
            verifyCustomers(companyIds)
                .then(response => resolve(response.data.validCompanyIds))
                .catch(err => reject(err.response.data.message)),
        )

    const verifyOneCustomer = (sapId, onSuccess, onError) => {
        return verifyCustomer(sapId)
            .then(onSuccess)
            .catch(onError)
    }

    const handleChangeSalesPersonStatus = data => {
        return changeSalesPersonStatus(data)
    }

    const handleDeleteAdmin = (userId, onDeleted, onError) => {
        return deleteAdmin(userId)
            .then(onDeleted)
            .catch(onError)
    }

    const handleToggleFeature = (feature, onToggled, onError) => {
        return toggleFeature(feature)
            .then(onToggled)
            .catch(onError)
    }

    const handleUsersReport = (onSuccess, onError) => {
        return getReportUsersInfo()
            .then(onSuccess)
            .catch(onError)
    }

    const handleUsersLoginReport = (onSuccess, onError) => {
        return getReportUsersLoginInfo()
            .then(onSuccess)
            .catch(onError)
    }

    const handleSalesRepLoginReport = (onSuccess, onError) => {
        return getReportSalesRepLoginInfo()
            .then(onSuccess)
            .catch(onError)
    }

    const handleLoadFeatureFlags = onLoaded => {
        return getFeatureFlags().then(onLoaded)
    }

    const handleChangeMaintenanceMode = (onChangedSuccess, onError) => {
        return changeMaintenanceMode()
            .then(onChangedSuccess)
            .catch(onError)
    }

    const handleUpdateMaintenanceModeData = (
        data,
        onUpdatedSuccess,
        onError,
    ) => {
        return updateMaintenanceModeData(data)
            .then(onUpdatedSuccess)
            .catch(onError)
    }

    const handleSearchUsersAndCompanies = (
        query,
        onSearch,
        onError,
        errorMessage,
    ) => {
        return searchUserAndCompanies(query)
            .then(onSearch)
            .catch(err => onError(err, errorMessage))
    }

    const handleGetUsersInfo = (params, onSuccess, onError, errorMessage) => {
        return getUsers(params)
            .then(onSuccess)
            .catch(err => onError(err, errorMessage))
    }

    const handleRefreshCompanyData = (
        data,
        onRefresh,
        onError,
        errorMessage,
    ) => {
        return refreshCompanyData(data)
            .then(onRefresh)
            .catch(err => onError(err, errorMessage))
    }

    const handleDeleteUser = (userInfo, onSuccess, onError, errorMessage) => {
        return deleteUser(userInfo)
            .then(() => onSuccess(userInfo?.userId))
            .catch(err => onError(err, errorMessage))
    }

    const handleDeleteCompany = (
        companyInfo,
        onSuccess,
        onError,
        errorMessage,
    ) => {
        return deleteCompany(companyInfo)
            .then(onSuccess)
            .catch(err => onError(err, errorMessage))
    }

    return {
        checkForValidCompanyIds,
        verifyOneCustomer,
        handleChangeSalesPersonStatus,
        handleDeleteAdmin,
        handleToggleFeature,
        handleLoadFeatureFlags,
        handleUsersReport,
        handleUsersLoginReport,
        handleSalesRepLoginReport,
        handleSearchUsersAndCompanies,
        handleGetUsersInfo,
        handleRefreshCompanyData,
        handleDeleteUser,
        handleDeleteCompany,
        handleChangeMaintenanceMode,
        handleUpdateMaintenanceModeData,
        fetchCompanies,
        updateUserManagementStatus,
        deleteCompanyStore,
        deleteUserStore,
        impersonateUser,
        stopImpersonation,
        forgotPassword,
        setCurrentCompanySapId,
        adminSuccess,
        adminHasErrored,
        adminIsLoading,
        companies: [...companies],
        currentCompanySapId,
        errors: [...errors],
    }
}

export default useAdmin
